import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import {
  InternalApiManualAlert,
  InternalApiAlertType,
  InternalApiCaseAttachmentType,
  InternalApiEntityType,
  InternalApiUniversalDocumentEntityType,
} from '../../api-internal';
import { manualAlertInternalApi, transactionApi } from '../../apis';
import { SalvProgressCircle } from '@salvapp/ui-components';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';
import { AlertManagement } from '../management/AlertManagement';
import { Documents } from '../../common/documents/Documents';
import SalvPage from '../../common/SalvPage';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { Card, Row, Col } from 'react-bootstrap';
import { PersonLink } from '../../common/PersonLink';
import { InternalNavigation } from '../../navigation/helper';
import { useMe } from '../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import PersonAttributes from '../../aml/sections/PersonAttributes';
import TransactionAttributes from '../../transaction/TransactionAttributes';
import { InternalApiTransaction } from '../../api-internal';
import { isCounterparty } from '../../common/helper';
import { useAlertFlow } from '../flow/useAlertFlow';
import { useSelector } from 'react-redux';
import { AppState } from '../../store/store';
import { getUserId } from '../../auth/authReducer';
import { getRationaleLabel } from './rationaleHelper';
import Has from '../../auth/Has';
import { CAN_READ_CASE } from '../../auth/permissions';
import AttachmentCases from '../../case/attachments/AttachmentCases';
import RFISending from '../../rfi-sending/RFISending';
import useRfiData from '../../rfi-sending/useRfiData';
import RFIAttributes from '../../rfi-sending/RFIAttributes';

export const ManualAlert = () => {
  const { features } = useMe();
  const [fetching, setFetching] = useState(true);
  const [alert, setAlert] = useState<InternalApiManualAlert | null>(null);
  const [transaction, setTransaction] = useState<InternalApiTransaction>();

  const activeUserId = useSelector((state: AppState) => getUserId(state));
  const alertId = Number(useParams<{ id: string }>().id);

  const [setDirty, resetBlocker, renderNavigationButtons] = useAlertFlow(
    alert?.assignee?.id == activeUserId,
    alertId,
    InternalApiAlertType.MANUAL
  );

  const fetchAlert = async () => {
    const alert = await manualAlertInternalApi.getManualAlert({
      id: alertId,
    });
    setAlert(alert);
  };

  const fetchTransaction = async (transactionId: string) => {
    const transaction = await transactionApi.findTransactionById({
      transactionId,
    });
    setTransaction(transaction);
  };

  const initialFetch = useCallback(async () => {
    resetBlocker();
    setFetching(true);
    try {
      await fetchAlert();
    } catch (_: unknown) {
      setAlert(null);
      return;
    } finally {
      setFetching(false);
    }
  }, [alertId]);

  useEffect(() => {
    initialFetch();
  }, [alertId, initialFetch]);

  useEffect(() => {
    if (!alert?.transaction?.id) return;
    fetchTransaction(alert.transaction.id);
  }, [alert]);

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  const isRfiEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features);
  const rfiData = useRfiData(alertId, InternalApiAlertType.MANUAL, isRfiEnabled);

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const renderRationaleCard = (alert: InternalApiManualAlert) => {
    return (
      <Card className='alert-rationale'>
        <Card.Header>
          <div className='title-s'>Alert details</div>
        </Card.Header>
        <Card.Body>
          <Row className='align-items-center'>
            <Col>
              <span className='text-strong-s'>Rationale</span>
            </Col>
            <Col md='auto'>
              <span className='content'>{getRationaleLabel(alert.rationale)}</span>
            </Col>
          </Row>
          {alert.rationaleComment && (
            <Row className='p-t-16'>
              <Col>
                <span className='text-strong-s'>Comment</span>
                <div className='content p-t-4'>{alert.rationaleComment}</div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderSubtitle = (alert: InternalApiManualAlert) => {
    if (!alert.person) return null;
    return (
      <div style={{ display: 'flex', gap: '24px' }}>
        <div>
          <span className='text-secondary m-r-4'>{isCounterparty(alert.person) ? 'Counterparty:' : 'Customer:'}</span>
          <PersonLink person={alert.person} mappings={attributeMappings?.map || {}} />
        </div>
        {alert.transaction && (
          <div>
            <span className='text-secondary m-r-4'>Transaction:</span>
            <Link to={InternalNavigation.transaction.replace(':id', alert.transaction.id)}>{alert.transaction.id}</Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <SalvPage
      title={`MANUAL Alert #${alertId}`}
      subTitle={alert ? renderSubtitle(alert) : undefined}
      actions={renderNavigationButtons()}
      isLoading={fetching}
    >
      {!fetching && alert === null && (
        <Card>
          <Card.Body>
            <div className='small'>Could not find alert with ID: {alertId}.</div>
          </Card.Body>
        </Card>
      )}
      {alert && (
        <Row>
          <Col md={4} className='sticky-col'>
            {transaction ? (
              <TransactionAttributes
                transaction={transaction}
                showLinkToTransactionPage={false}
                showLinkToCustomerPage={false}
              />
            ) : (
              alert.person && <PersonAttributes person={alert.person} displayCustomerLink={false} />
            )}
            {isRfiEnabled && <RFIAttributes rfiData={rfiData} />}
          </Col>
          <Col md={8}>
            <AlertManagement
              alertId={alertId}
              alertType={InternalApiAlertType.MANUAL}
              entityType={alert.transaction ? InternalApiEntityType.TRANSACTION : InternalApiEntityType.PERSON}
              postAlertPropertyChange={setDirty}
            />
            {isRfiEnabled && (
              <RFISending
                alert={alert}
                alertType={InternalApiAlertType.MANUAL}
                rfiData={rfiData}
                onChange={() => initialFetch()}
              />
            )}
            {renderRationaleCard(alert)}
            <Documents
              className='universal-documents--legacy'
              entityType={InternalApiUniversalDocumentEntityType.MANUAL_ALERT}
              entityId={alertId}
            />
            {isFeatureEnabled(ORGANISATION_FEATURE.CASE_MANAGEMENT, features) && (
              <Has allPermissions={[CAN_READ_CASE]}>
                <AttachmentCases
                  attachmentType={InternalApiCaseAttachmentType.MANUAL_ALERT}
                  externalId={String(alertId)}
                />
              </Has>
            )}
          </Col>
        </Row>
      )}
    </SalvPage>
  );
};

export default ManualAlert;
